/** Valores gravados na API / base (ENUM). */
export const ORDER_STATUS_VALUES = ['pending', 'paid', 'sent_to_erp', 'cancelled'];

/** Ordem dos botões no painel «Pedidos» (valores da API; só a ordem na UI muda). */
export const ORDER_STATUS_BUTTON_ORDER = ['pending', 'sent_to_erp', 'paid', 'cancelled'];

/** Rótulos em português do Brasil para a UI (valores da API mantêm-se em inglês). */
export const ORDER_STATUS_LABEL_PT_BR = {
  pending: 'Pendente',
  paid: 'Faturado',
  sent_to_erp: 'Enviado ao sistema',
  cancelled: 'Cancelado',
};

/** @param {string} [value] */
export function orderStatusLabelPtBr(value) {
  const k = typeof value === 'string' ? value.trim() : '';
  if (!k) return '';
  return ORDER_STATUS_LABEL_PT_BR[k] ?? k;
}

/** Estado da loja na tabela `tenants` (ENUM). */
export function tenantShopStatusLabelPtBr(value) {
  const k = typeof value === 'string' ? value.trim() : '';
  if (k === 'active') return 'Ativa';
  if (k === 'blocked') return 'Bloqueada';
  if (!k) return '—';
  return k;
}

/**
 * Pagamento confirmado pela loja via gateway online (ex.: webhook Mercado Pago).
 *
 * IMPORTANTE — política da OminIA:
 *   - Esta verificação é INDEPENDENTE de `order.status`.
 *   - O facto de o cliente ter pago online vive apenas em
 *     `mp_payment_id` + `mp_payment_status='approved'`, gravados pelo
 *     webhook MP. O status do pedido (botão «Faturado») só passa a `paid`
 *     quando o ERP confirma a emissão da nota fiscal via API.
 *   - Outros gateways online podem somar condições aqui no futuro.
 *
 * @param {{ mp_payment_id?: string | null, mp_payment_status?: string | null } | null | undefined} order
 */
export function orderIsPaidViaOnlineGateway(order) {
  if (!order) return false;
  const id = order.mp_payment_id;
  if (typeof id !== 'string' || id.trim() === '') return false;
  const st =
    typeof order.mp_payment_status === 'string' ? order.mp_payment_status.trim().toLowerCase() : '';
  // Vazio = pedidos antigos onde só era persistido `mp_payment_id`. Mantemos
  // compatibilidade (presença do id já indicava aprovação na lógica antiga).
  return st === '' || st === 'approved';
}

/**
 * Tenta extrair `payment_state` (database/0044_payment_state.sql) do pedido, em
 * formato normalizado. Aceita variações em `payment_state`/`paymentState` e
 * trata vazio/null. Retorna `''` quando o backend ainda não devolveu a flag
 * (deploy antigo / migration por importar) — os caminhos de UI a jusante
 * encaixam-se de forma defensiva: sem `payment_state`, o comportamento volta
 * exactamente ao que era antes da feature.
 *
 * @param {Record<string, unknown> | null | undefined} order
 * @returns {'' | 'awaiting_gateway' | 'paid' | 'expired' | 'failed' | 'not_required'}
 */
export function orderPaymentStateOf(order) {
  if (!order || typeof order !== 'object') return '';
  const raw =
    order.payment_state ??
    /** @type {Record<string, unknown>} */ (order).paymentState ??
    '';
  if (typeof raw !== 'string') return '';
  const k = raw.trim().toLowerCase();
  if (
    k === 'awaiting_gateway' ||
    k === 'paid' ||
    k === 'expired' ||
    k === 'failed' ||
    k === 'not_required'
  ) {
    return /** @type {'awaiting_gateway' | 'paid' | 'expired' | 'failed' | 'not_required'} */ (k);
  }
  return '';
}

/**
 * Calcula o "estado prático" do pagamento online do ponto de vista da UI do
 * admin. Funde 2 fontes:
 *   - `mp_payment_status` (webhook MP — verdade do gateway)
 *   - `payment_state` (database/0044_payment_state.sql — pode estar mais
 *      adiantado que o webhook quando o cron já reconciliou)
 *
 * Regras (a primeira que case ganha):
 *   1. Pagamento confirmado (`orderIsPaidViaOnlineGateway` OU payment_state=paid) → 'paid'
 *   2. payment_state explícito ('awaiting_gateway' / 'expired' / 'failed')
 *   3. Pedido pediu MP (`mp_preference_id` presente) sem confirmação → 'awaiting_gateway' (compat)
 *   4. Não passou por MP → '' (vazio)
 *
 * @param {Record<string, unknown> | null | undefined} order
 * @returns {'' | 'awaiting_gateway' | 'paid' | 'expired' | 'failed'}
 */
export function orderOnlinePaymentEffectiveState(order) {
  if (!order || typeof order !== 'object') return '';
  const ps = orderPaymentStateOf(order);
  if (orderIsPaidViaOnlineGateway(order) || ps === 'paid') return 'paid';
  if (ps === 'awaiting_gateway') return 'awaiting_gateway';
  if (ps === 'expired') return 'expired';
  if (ps === 'failed') return 'failed';
  if (ps === 'not_required') return '';

  // Sem `payment_state` (deploy antigo): cai no critério legado — pediu MP mas
  // não confirmou ainda → aguardando.
  const pref =
    typeof /** @type {Record<string, unknown>} */ (order).mp_preference_id === 'string'
      ? /** @type {string} */ (/** @type {Record<string, unknown>} */ (order).mp_preference_id).trim()
      : '';
  if (pref !== '') return 'awaiting_gateway';
  return '';
}

/**
 * Devolve o tempo restante (segundos) até o cron de reconciliação considerar
 * o pedido abandonado, ou `null` se não houver deadline.
 *
 * @param {Record<string, unknown> | null | undefined} order
 * @param {number} [nowMs]
 */
export function orderPaymentDeadlineRemainingSec(order, nowMs) {
  if (!order || typeof order !== 'object') return null;
  const raw =
    /** @type {Record<string, unknown>} */ (order).payment_gateway_deadline ??
    /** @type {Record<string, unknown>} */ (order).paymentGatewayDeadline ??
    null;
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  // Trata como hora local do servidor (Y-m-d H:i:s) — Date.parse aceita ambos.
  const ts = Date.parse(raw.replace(' ', 'T'));
  if (!Number.isFinite(ts)) return null;
  const ref = Number.isFinite(nowMs) ? Number(nowMs) : Date.now();
  return Math.round((ts - ref) / 1000);
}

/**
 * Versão humana ("em 18 min" / "há 4 min") para mostrar em tooltips.
 *
 * @param {number | null | undefined} sec
 */
export function formatPaymentDeadlineHuman(sec) {
  if (sec == null || !Number.isFinite(sec)) return '';
  const abs = Math.abs(sec);
  const m = Math.max(1, Math.round(abs / 60));
  if (m < 60) return sec >= 0 ? `em ${m} min` : `há ${m} min`;
  const h = Math.round(m / 60);
  return sec >= 0 ? `em ${h} h` : `há ${h} h`;
}

/**
 * Adapta um `payment_state` (efectivo) ao que a UI precisa para renderizar um
 * chip na linha do pedido: rótulo natural, tom de cor, intenção de clique e
 * tooltip humano. Devolve `null` quando NADA deve ser mostrado (i.e., pedido
 * não envolveu gateway online).
 *
 * @param {Record<string, unknown> | null | undefined} order
 * @param {{ now?: number }} [opts]
 */
export function paymentStateChipDescriptor(order, opts) {
  const eff = orderOnlinePaymentEffectiveState(order);
  if (!eff) return null;

  const remaining = orderPaymentDeadlineRemainingSec(order, opts?.now);
  const remainingHuman = formatPaymentDeadlineHuman(remaining);

  switch (eff) {
    case 'paid':
      // Quando pago, o botão `$ Online` verde já comunica o estado — chip extra
      // seria ruído. Devolvemos null para a UI esconder o chip.
      return null;
    case 'awaiting_gateway':
      return {
        state: 'awaiting_gateway',
        label: 'Aguardando MP',
        icon: '⏳',
        // tom "warning" suave (laranja/âmbar)
        tone: 'warn',
        clickable: true,
        clickIntent: 'reconcile',
        tooltip:
          'Cliente foi para o Mercado Pago e ainda não confirmou o pagamento.' +
          (remaining != null && remaining > 0
            ? ` O sistema verifica automaticamente ${remainingHuman}.`
            : remaining != null
            ? ` Já passou da janela de espera (${remainingHuman}).`
            : '') +
          ' Clique para verificar agora.',
      };
    case 'expired':
      return {
        state: 'expired',
        label: 'MP expirou',
        icon: '⏰',
        tone: 'muted',
        clickable: false,
        clickIntent: 'info',
        tooltip:
          'Cliente foi para o Mercado Pago mas não pagou dentro do prazo. O pedido foi cancelado automaticamente, o estoque devolvido e os itens repostos na sacola do cliente.',
      };
    case 'failed':
      return {
        state: 'failed',
        label: 'MP recusado',
        icon: '⛔',
        tone: 'danger',
        clickable: false,
        clickIntent: 'info',
        tooltip:
          'O Mercado Pago recusou o pagamento (cartão negado, fundos insuficientes, etc.). O pedido foi cancelado automaticamente.',
      };
    default:
      return null;
  }
}

/**
 * Variante do descritor para a página "A minha conta" do cliente final (na
 * vitrine pública). Difere do `paymentStateChipDescriptor` em 3 aspectos:
 *
 *   1. **Linguagem**: zero jargão técnico ("MP", "expirou", "recusado").
 *      Foco em "o que aconteceu comigo / o que faço agora".
 *   2. **Mostra `paid`**: o cliente quer ver "Pagamento confirmado" no histórico
 *      dele, ao contrário do admin que já tem o "$ Online" verde a comunicar.
 *   3. **Tons**: usa as mesmas paletas mas a intenção de clique no awaiting
 *      direcciona para "tentar pagar de novo" em vez de "reconciliar".
 *
 * @param {Record<string, unknown> | null | undefined} order
 */
export function paymentStateCustomerChipDescriptor(order) {
  const eff = orderOnlinePaymentEffectiveState(order);
  if (!eff) return null;

  switch (eff) {
    case 'paid':
      return {
        state: 'paid',
        label: 'Pagamento confirmado',
        icon: '✅',
        tone: 'success',
        clickable: false,
        clickIntent: 'info',
        tooltip:
          'Pagamento online recebido pelo Mercado Pago. A loja vai preparar e enviar o seu pedido.',
      };
    case 'awaiting_gateway':
      return {
        state: 'awaiting_gateway',
        label: 'Aguardando seu pagamento',
        icon: '⏳',
        tone: 'warn',
        clickable: false,
        clickIntent: 'info',
        tooltip:
          'O pagamento ainda não foi confirmado pelo Mercado Pago. Se já pagou, aguarde alguns minutos — o sistema verifica automaticamente. Se ainda não pagou, abra o Mercado Pago e finalize.',
      };
    case 'expired':
      return {
        state: 'expired',
        label: 'Pagamento não concluído',
        icon: '⏰',
        tone: 'muted',
        clickable: false,
        clickIntent: 'info',
        tooltip:
          'O pagamento online não foi finalizado dentro do prazo. O pedido foi cancelado automaticamente e os itens voltaram para a sua sacola — basta abrir a loja e finalizar novamente quando quiser.',
      };
    case 'failed':
      return {
        state: 'failed',
        label: 'Pagamento recusado',
        icon: '⛔',
        tone: 'danger',
        clickable: false,
        clickIntent: 'info',
        tooltip:
          'O Mercado Pago recusou o pagamento (cartão negado, saldo insuficiente, etc.). O pedido foi cancelado e os itens voltaram para a sua sacola. Tente outro meio de pagamento na hora de finalizar.',
      };
    default:
      return null;
  }
}
